/**
 * Positioning for anchored overlays (popovers, menus, tooltips). Overlays are portalled
 * to `document.body` with `position: fixed` and placed next to their anchor, flipping to
 * the opposite side when they would leave the viewport and clamped to it otherwise.
 */
import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';
export type Placement = Side | `${Side}-${Exclude<Align, 'center'>}`;

const VIEWPORT_MARGIN = 8;

interface Size {
  width: number;
  height: number;
}

function splitPlacement(placement: Placement): [Side, Align] {
  const [side, align] = placement.split('-') as [Side, Exclude<Align, 'center'> | undefined];
  return [side, align ?? 'center'];
}

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

/** Top-left (viewport coordinates) of a `floating`-sized box placed around `anchor`. */
export function computePosition(
  anchor: DOMRect,
  floating: Size,
  placement: Placement,
  offset: number,
  viewport: Size,
): { top: number; left: number; side: Side } {
  const [preferred, align] = splitPlacement(placement);
  const fits = (side: Side): boolean => {
    switch (side) {
      case 'top':
        return anchor.top - offset - floating.height >= VIEWPORT_MARGIN;
      case 'bottom':
        return anchor.bottom + offset + floating.height <= viewport.height - VIEWPORT_MARGIN;
      case 'left':
        return anchor.left - offset - floating.width >= VIEWPORT_MARGIN;
      case 'right':
        return anchor.right + offset + floating.width <= viewport.width - VIEWPORT_MARGIN;
    }
  };
  const side = fits(preferred) || !fits(OPPOSITE[preferred]) ? preferred : OPPOSITE[preferred];

  let top: number;
  let left: number;
  if (side === 'top' || side === 'bottom') {
    top = side === 'top' ? anchor.top - offset - floating.height : anchor.bottom + offset;
    left =
      align === 'start'
        ? anchor.left
        : align === 'end'
          ? anchor.right - floating.width
          : anchor.left + anchor.width / 2 - floating.width / 2;
  } else {
    left = side === 'left' ? anchor.left - offset - floating.width : anchor.right + offset;
    top =
      align === 'start'
        ? anchor.top
        : align === 'end'
          ? anchor.bottom - floating.height
          : anchor.top + anchor.height / 2 - floating.height / 2;
  }
  const clamp = (value: number, size: number, max: number) =>
    Math.max(VIEWPORT_MARGIN, Math.min(value, max - size - VIEWPORT_MARGIN));
  return {
    top: clamp(top, floating.height, viewport.height),
    left: clamp(left, floating.width, viewport.width),
    side,
  };
}

/**
 * Style for a fixed-position overlay anchored to `anchorRef`, recomputed on open, resize,
 * scroll (any ancestor) and when the overlay's own size changes. Hidden until measured.
 */
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  open: boolean,
  placement: Placement,
  offset = 6,
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const anchor = anchorRef.current;
      const floating = floatingRef.current;
      if (!anchor || !floating) return;
      const { top, left } = computePosition(
        anchor.getBoundingClientRect(),
        { width: floating.offsetWidth, height: floating.offsetHeight },
        placement,
        offset,
        { width: window.innerWidth, height: window.innerHeight },
      );
      setStyle({ position: 'fixed', top, left, visibility: 'visible' });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (floatingRef.current) observer?.observe(floatingRef.current);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      observer?.disconnect();
    };
  }, [anchorRef, floatingRef, open, placement, offset]);

  return style;
}
